import { createSlice } from '@reduxjs/toolkit';
import {
  InternationalVerificationBulkDetails,
  InternationalVerificationDetails,
  InternationalVerificationRecord,
  PaginatedResponse,
  VerificationResponse,
} from '../avs.types';
import {
  uploadInternationalBulkVerification,
  getInternationalVerificationRecords,
  getInternationalBulkVerificationResult,
  getInternationalBulkVerificationResultData,
  getInternationalVerificationResult,
} from './avsInternationalVerification.actions';

export interface AvsInternationalVerificationState {
  internationalVerificationRecords: PaginatedResponse<InternationalVerificationRecord> | null;
  internationalBulkVerificationResponse: VerificationResponse<never> | null;
  internationalBulkVerificationResult: InternationalVerificationBulkDetails | null;
  internationalBulkVerificationResultData: PaginatedResponse<InternationalVerificationDetails> | null;
  internationalVerificationResultDetail: InternationalVerificationDetails | null;
  isFetchingInternationalVerificationRecords: boolean;
  isFetchingInternationalVerificationResult: boolean;
  isFetchingInternationalVerificationResultData: boolean;
}

const initialState: AvsInternationalVerificationState = {
  internationalVerificationRecords: null,
  internationalBulkVerificationResponse: null,
  internationalBulkVerificationResult: null,
  internationalBulkVerificationResultData: null,
  internationalVerificationResultDetail: null,
  isFetchingInternationalVerificationRecords: false,
  isFetchingInternationalVerificationResult: false,
  isFetchingInternationalVerificationResultData: false,
};

const avsInternationalVerificationSlice = createSlice({
  name: 'avsInternationalVerification',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(uploadInternationalBulkVerification.pending, state => {
      state.internationalBulkVerificationResponse = null;
      state.isFetchingInternationalVerificationResult = true;
    });
    builder.addCase(
      uploadInternationalBulkVerification.fulfilled,
      (state, { payload }) => {
        state.internationalBulkVerificationResponse =
          payload as unknown as VerificationResponse<never>;
        state.isFetchingInternationalVerificationResult = false;
      },
    );
    builder.addCase(
      uploadInternationalBulkVerification.rejected,
      (state, { payload }) => {
        state.internationalBulkVerificationResponse =
          payload as VerificationResponse<never>;
        state.isFetchingInternationalVerificationResult = false;
      },
    );

    builder.addCase(getInternationalVerificationRecords.pending, state => {
      state.isFetchingInternationalVerificationRecords = true;
    });
    builder.addCase(
      getInternationalVerificationRecords.fulfilled,
      (state, { payload }) => {
        state.isFetchingInternationalVerificationRecords = false;
        state.internationalVerificationRecords = payload;
      },
    );
    builder.addCase(getInternationalVerificationRecords.rejected, state => {
      state.isFetchingInternationalVerificationRecords = false;
    });

    builder.addCase(getInternationalBulkVerificationResult.pending, state => {
      state.isFetchingInternationalVerificationResult = true;
    });
    builder.addCase(
      getInternationalBulkVerificationResult.fulfilled,
      (state, { payload }) => {
        state.isFetchingInternationalVerificationResult = false;
        state.internationalBulkVerificationResult = payload;
      },
    );
    builder.addCase(getInternationalBulkVerificationResult.rejected, state => {
      state.isFetchingInternationalVerificationResult = false;
    });

    builder.addCase(
      getInternationalBulkVerificationResultData.pending,
      (state, { meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingInternationalVerificationResultData = true;
        }
      },
    );
    builder.addCase(
      getInternationalBulkVerificationResultData.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingInternationalVerificationResultData = false;
          state.internationalBulkVerificationResultData = payload;
        }
      },
    );
    builder.addCase(
      getInternationalBulkVerificationResultData.rejected,
      state => {
        state.isFetchingInternationalVerificationResultData = false;
      },
    );

    builder.addCase(
      getInternationalVerificationResult.pending,
      (state, { meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingInternationalVerificationResult = true;
        }
      },
    );
    builder.addCase(
      getInternationalVerificationResult.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingInternationalVerificationResult = false;
          state.internationalVerificationResultDetail = payload;
        }
      },
    );
    builder.addCase(getInternationalVerificationResult.rejected, state => {
      state.isFetchingInternationalVerificationResult = false;
    });
  },
});

export default avsInternationalVerificationSlice.reducer;
